import { ErrorMessage, StringPropertyTextBlock, VList, VRootObject } from "@1oo4/miv";
import { MRootDoc } from "../../shared/model/masterdata";
import { DocClient } from "../../shared/docclient";
import { handleError, MIVPage } from "../../shared/pages";
import "./doc.css";
import { CodeLanguage, MArticle, MChapter, MSection } from "../../shared/model/mdoc";
import { VArticleDoc, VChapterDoc, VSectionDoc } from "./views";

export class DocPage extends MIVPage {

    root: VRootDoc | undefined;
    readonly panelChapters: HTMLDivElement;
    readonly panelSections: HTMLDivElement;
    private readonly vertiLab: StringPropertyTextBlock;
    private readonly bMenu: HTMLButtonElement;
    private readonly menu: HTMLDivElement;

    private selected_article: VArticleDoc | undefined;
    private must_collapse: boolean = false;

    private readonly regxArticle: RegExp;
    private readonly regxSection: RegExp;

    private focus_cs_rather_than_ts: boolean = true;

    constructor() {
        super("page_doc", "doc", "nav_doc", "nav_doc_short");
        this.panelChapters = document.getElementById("page_doc_chapters") as HTMLDivElement
        this.panelSections = document.getElementById("page_doc_sections") as HTMLDivElement
        this.vertiLab = new StringPropertyTextBlock(document.getElementById("page_doc_selected_vertical") as HTMLSpanElement);

        this.menu = document.getElementById("doc_menu_column") as HTMLDivElement
        this.menu.onclick = () => {
            if (this.must_collapse)
                this.expand();
        }

        this.bMenu = document.getElementById("doc_collexp") as HTMLButtonElement;
        const self = this;
        this.bMenu.onclick = (evt) => {
            if (self.must_collapse) self.expand();
            else self.collapse();
            evt.stopPropagation();
        }

        this.expand();

        this.panelSections.addEventListener("click", (evt) => {
            self.collapseIfTooNarrow();
        });

        this.regxArticle = new RegExp("^/doc/article/([0-9]+)/?");
        this.regxSection = new RegExp("^/doc/section/([0-9]+)/?");
    }

    async OpenAsync(): Promise<void> {
        if (this.root === undefined) {
            const client = await DocClient.getClientAsync();
            if (client instanceof ErrorMessage) {
                handleError(client, true);
                return;
            }
            this.root = new VRootDoc(client.DocRoot, this);
            await this.root.DisplayAsync();
        }
    }
    Close(): void {
        //this.root?.HideAsync();
    }

    private static slugify(text: string | undefined): string {
        if (text === undefined || text.length <= 0) return "";
        return text
            .normalize("NFD")                    // decompose accented characters: "é" → "e" + "́"
            .replace(/[\u0300-\u036f]/g, "")     // strip accent marks
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, "")       // remove anything that isn't a letter, digit, space or hyphen
            .replace(/\s+/g, "-")               // replace spaces with hyphens
            .replace(/-+/g, "-");               // collapse multiple hyphens into one
    }
    static getArticleURL(m: MArticle) {
        return "/doc/article/" + m.ID + "/" + DocPage.slugify(m.Name.Value);
    }
    static getSectionURL(m: MSection) {
        return "/doc/section/" + m.ID + "/" + DocPage.slugify(m.Parent.Name.Value) + "/" + DocPage.slugify(m.Name.Value);
    }

    protected async ResolvePathAndNavigateAsync(path: string, samePathAsCurrent: boolean): Promise<void> {
        if (this.root !== undefined && path != "/doc" && path != "/doc/") {
            const article = this.regxArticle.exec(path);
            if (article != null && article.length >= 2) {
                const articleID = BigInt(Number(article[1]));
                for (let i = 0; i < this.root.chapters.Count; i++) {
                    const chap = this.root.chapters.Get(i);
                    for (let j = 0; j < chap.articles.Count; j++) {
                        const art = chap.articles.Get(j);
                        if (art.Model.ID == articleID) {
                            this.expand();
                            chap.expand();
                            await this.selectArticleAsync(art, false);
                            if (!samePathAsCurrent)
                                art.bringIntoView(true);
                            return;
                        }
                    }
                }
                this.navigateFail(path);
                return;
            }
            const section = this.regxSection.exec(path);
            if (section != null && section.length >= 2) {
                const sectioniID = BigInt(Number(section[1]));
                for (let i = 0; i < this.root.chapters.Count; i++) {
                    const chap = this.root.chapters.Get(i);
                    for (let j = 0; j < chap.articles.Count; j++) {
                        const art = chap.articles.Get(j);
                        for (let k = 0; k < art.Model.Sections.Count; k++) {
                            const mSec = art.Model.Sections.Get(k);
                            if (mSec.ID == sectioniID) {
                                this.expand();
                                chap.expand();
                                await this.selectArticleAsync(art, false);
                                art.bringIntoView(false);
                                const vSec = art.sections.GetViewOfModel(mSec);
                                if (vSec === undefined) {
                                    alert("cannot find the required Section");
                                } else if (!samePathAsCurrent) {
                                    this.markMustCollapseIfTooNarrow();
                                    this.updateDisplay();
                                    vSec.bringIntoView(true);
                                }
                                return;
                            }
                        }
                    }
                }
            }
            this.navigateFail(path);
            return;
        }
    }
    private navigateFail(path: string): void {
        alert("cannot find the required path : " + path);
        this.RevertToCurrentPath();
    }

    get selectedArticle(): VArticleDoc | undefined {
        return this.selected_article;
    }
    async selectArticleAsync(value: VArticleDoc | undefined, setPath: boolean): Promise<void> {
        if (value != this.selected_article) {
            if (this.selected_article !== undefined) {
                this.selected_article.setSelected(false);
            }
            this.selected_article = value;
            if (value === undefined) {
                this.vertiLab.Deactivate();
            } else {
                await value.setSelected(true);
                this.vertiLab.Setup(value.Model.Name, value);
                if (setPath) {
                    this.SetCurrentPath(DocPage.getArticleURL(value.Model), !this.isCurrentPathBase());
                }
                this.markMustCollapseIfTooNarrow();
            }
            this.updateDisplay();
        } else if (value !== undefined && setPath) {
            this.markMustCollapseIfTooNarrow();
        }
    }

    selectSectionWithinSelectedArticle(s: VSectionDoc) {
        this.collapseIfTooNarrow();
        s.bringIntoView(true);
    }

    private collapseIfTooNarrow() {
        if (this.isTooNarrow()) {
            this.must_collapse = true;
            this.updateDisplay();
        }
    }
    private isTooNarrow(): boolean {
        const menuWidthMultiplied = 2 * this.menu.offsetWidth;
        return menuWidthMultiplied >= window.innerWidth || menuWidthMultiplied >= window.screen.width;
    }
    private markMustCollapseIfTooNarrow() {
        if (this.isTooNarrow())
            this.must_collapse = true;
    }

    private collapse(): void {
        this.must_collapse = true;
        this.updateDisplay();
    }
    expand(): void {
        this.must_collapse = false;
        this.updateDisplay();
    }

    private updateDisplay(): void {
        if (this.selected_article === undefined) {
            this.vertiLab.HTMLSpan.style.display = "none";
            this.panelChapters.style.display = '';
            this.bMenu.style.display = "none";
        } else {
            this.bMenu.style.display = "block";
            if (this.must_collapse) {
                this.vertiLab.HTMLSpan.style.display = "block";
                this.panelChapters.style.display = "none";
            } else {
                this.vertiLab.HTMLSpan.style.display = "none";
                this.panelChapters.style.display = '';
            }
        }
    }

    get focusCSratherThanTS(): boolean { return this.focus_cs_rather_than_ts; }
    focusLanguage(lang: CodeLanguage) {
        if (lang == CodeLanguage.CS || lang == CodeLanguage.XAML) {
            this.focus_cs_rather_than_ts = true;
        } else if (lang == CodeLanguage.TS || lang == CodeLanguage.HTML) {
            this.focus_cs_rather_than_ts = false;
        }
        if (this.root) {
            for (let ic = 0; ic < this.root.chapters.Count; ic++) {
                const chap = this.root.chapters.Get(ic);
                for (let ia = 0; ia < chap.articles.Count; ia++) {
                    const arti = chap.articles.Get(ia);
                    for (let is = 0; is < arti.sections.Count; is++) {
                        const sect = arti.sections.Get(is);
                        for (let i = 0; i < sect.contents.Count; i++) {
                            sect.contents.Get(i).focusLanguage(lang);
                        }
                    }
                }
            }
        }
    }
}

export class VRootDoc extends VRootObject<MRootDoc> {

    readonly page: DocPage;
    readonly chapters: VList<VChapterDoc, MChapter>

    constructor(m: MRootDoc, page: DocPage) {
        super(m);
        this.page = page;
        this.chapters = new VList(m.Chapters, this, x => new VChapterDoc(x, this));
    }
}

